import { useCustomerDetails, useUnlinkAchAccount } from '@egw/api'
import { Button, ButtonVariant } from '@egw/components'
import { CUSTOMER_SERVICE_PHONE_NUMBER } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { useState, type FC } from 'react'

const log = new Logger('UpdatePasskeyForm')

export interface RemoveAccountFormProps {
  alias: number
  onSuccess: () => void
  onCloseModal: () => void
}

export const RemoveAccountForm: FC<RemoveAccountFormProps> = ({ alias, onCloseModal, onSuccess }) => {
  const [isRemovingAccountLoading, setIsRemovingAccountLoading] = useState(false)
  const [formError, setFormError] = useState(false)

  const { t } = useTranslation('features-external-accounts')

  const { data, refetch } = useCustomerDetails()
  const account = data?.customer.linkedAccounts.achAccounts.find((a) => a.alias === alias)

  const { mutate: removeAccount } = useUnlinkAchAccount({
    onError: () => {
      setFormError(true)
    },
  })

  const onRemoveAccount = async (): Promise<void> => {
    setFormError(false)
    setIsRemovingAccountLoading(true)
    try {
      await removeAccount({ alias })
      await refetch()
      onSuccess()
    } catch (error) {
      log.error(error)
      setFormError(true)
    } finally {
      setIsRemovingAccountLoading(false)
    }
  }

  return (
    <>
      {formError ? (
        <div className="flex bg-alertErrorBg p-3 mb-4 rounded-md">
          <InformationCircleIcon className="w-6 h-6 fill-red-500" />
          <p className="text-sm text-alertErrorText ml-2">
            {t('details.update_error')}{' '}
            <a
              className="text-btnLinkText hover:text-btnLinkTextHover hover:underline font-semibold"
              href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
            >
              {t('details.contact')}
            </a>
          </p>
        </div>
      ) : null}

      {account ? (
        <>
          <p className="mb-xs">{t('details.remove_message', { nickname: account.name })}</p>
          <Button
            variant={ButtonVariant.Danger}
            onClick={() => void onRemoveAccount()}
            testId="removeAccount_destruction_btn"
            className="mb-2xs"
            isLoading={isRemovingAccountLoading}
          >
            {t('details.remove_title')}
          </Button>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => {
              onCloseModal()
            }}
            isDisabled={isRemovingAccountLoading}
            testId="closeModal_secondary_btn"
          >
            {t('details.cancel')}
          </Button>
        </>
      ) : null}
    </>
  )
}
